/**
 * NodeConfigPanel — Popup centralizado de configuração de nodes.
 *
 * Usa o ConfigRegistry para renderizar o formulário correto baseado no
 * nodeType do node selecionado. Mesmo padrão visual do NodeSelector (popup).
 */

import React, { Suspense, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { FiX } from 'react-icons/fi';
import type { AvailableContext, WorkflowNodeCancellation } from '@troia-v3/types';
import { nodeTypeAcceptsFilters } from '@troia-v3/types';
import { Icon } from '@/shared/icons';
import type { IconName } from '@/shared/icons';
import { Input } from '@/shared/components/ui';

import { getConfigComponent } from './ConfigRegistry';
import { EMPTY_CONTEXT } from './types';
import type { SavedVariable } from './types';
import { NodeConfigTabs } from './NodeConfigTabs';
import type { NodeConfigTab } from './NodeConfigTabs';
import { CancellationRulesEditor } from './CancellationRulesEditor';
import { FilterBuilder } from '../FilterBuilder';
import type { FilterCondition } from '../FilterRow';
import { getNodeTypeLabel } from '../../utils/node-labels';
import { filterEntitiesForTrigger } from '../../utils/event-entities';
import { entidadesParaFiltro } from '../../utils/filter-entities';

/**
 * Gatilhos que guardam o filtro em `config.filters`, aplicado no DISPARO
 * (Task 13, spec 2026-09-14) — continuam com a própria mini-aba
 * (`TriggerFiltersTab`), lendo o evento escolhido na aba Geral. Os demais
 * nodes filtráveis (`nodeTypeAcceptsFilters`, @troia-v3/types) guardam o
 * filtro em `data.filters`, avaliado ANTES de o nó rodar — `NodeFiltersTab`,
 * Fase 3 (2026-09-16).
 */
const TRIGGERS_WITH_FILTERS = new Set(['trigger_event', 'trigger_date_field', 'trigger_inactivity']);

/**
 * TriggerFiltersTab — aba "Filtros" dos 3 gatilhos acima. Extraído de dentro
 * de cada componente de gatilho (que antes tinha sua própria mini-aba) para
 * o painel — os três liam `config.filters` do mesmo jeito.
 */
const TriggerFiltersTab: React.FC<{
  nodeType: string;
  config: Record<string, unknown>;
  onChange: (config: Record<string, unknown>) => void;
}> = ({ nodeType, config, onChange }) => {
  const entities = filterEntitiesForTrigger(nodeType, config);
  const eventType = nodeType === 'trigger_event' ? String(config.eventType ?? '') : '';

  if (entities.length === 0) {
    return (
      // Sem entidade não há campo para filtrar, e o FilterBuilder criaria uma
      // linha de texto livre que NUNCA casa — pior que não oferecer.
      <p className="text-xs" data-testid="event-trigger-no-filters" style={{ color: 'var(--theme-muted-text)' }}>
        {eventType === 'calendar_event.cancelled'
          ? 'Evento de agenda cancelado não tem filtros: o compromisso é excluído antes de o evento ser publicado, então os campos dele não chegam ao fluxo. Use os dados de {{triggerData}} nos nós seguintes.'
          : eventType
            ? 'Este evento não traz uma entidade para filtrar. Use os dados de {{triggerData}} nos nós seguintes.'
            : 'Escolha o tipo de evento na aba Geral para ver os filtros disponíveis.'}
      </p>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>
        O workflow só executará se todos os filtros forem atendidos.
      </p>
      <FilterBuilder
        filters={(config.filters as FilterCondition[]) ?? []}
        onChange={(filters) => onChange({ ...config, filters })}
        availableEntities={entities}
      />
    </div>
  );
};

/**
 * NodeFiltersTab — aba "Filtros" dos nós de ação/IA/aguardar (Fase 3, spec
 * 2026-09-14 §5.3): lê/grava `data.filters` (não `config`). Só roda se todos
 * os filtros forem atendidos — sem casar, o fluxo encerra ali (aquele
 * caminho) e o nó aparece como "Não rodou" no histórico.
 */
const NodeFiltersTab: React.FC<{
  filters: FilterCondition[];
  onChange: (next: FilterCondition[] | undefined) => void;
  context: AvailableContext;
  belowCancelledOutput: boolean;
}> = ({ filters, onChange, context, belowCancelledOutput }) => {
  const entities = entidadesParaFiltro(context, belowCancelledOutput);

  return (
    <div className="space-y-3">
      <p className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>
        Só roda se todos os filtros forem atendidos. Se não, o fluxo encerra aqui.
      </p>
      <FilterBuilder
        filters={filters}
        onChange={(next) => onChange(next.length > 0 ? next : undefined)}
        availableEntities={entities}
      />
    </div>
  );
};

// ============================================================================
// Props
// ============================================================================

interface NodeConfigPanelProps {
  nodeId: string;
  nodeType: string;
  nodeLabel: string;
  config: Record<string, unknown>;
  onChange: (config: Record<string, unknown>) => void;
  onLabelChange?: (label: string) => void;
  onClose: () => void;
  availableContext?: AvailableContext;
  /** Aba Cancelamento (todo node) — spec 2026-09-14, D1. */
  cancellation?: WorkflowNodeCancellation;
  onCancellationChange: (next: WorkflowNodeCancellation | undefined) => void;
  /**
   * Aba Filtros dos nós não-gatilho (`data.filters`) — Fase 3, spec
   * 2026-09-14 §5.3. Só recebido/relevante quando `nodeTypeAcceptsFilters`.
   */
  filters?: FilterCondition[];
  onFiltersChange?: (next: FilterCondition[] | undefined) => void;
  /**
   * O nó está depois da saída "Cancelado" de algum Aguardar? Só ali o bloco
   * `{{cancelamento.*}}` existe em runtime (spec 2026-09-20 §7.2/§7.7).
   */
  belowCancelledOutput?: boolean;
  /** Variáveis gravadas por nós anteriores (`saveAs`) — ver `savedVariablesBefore`. */
  savedVariables?: SavedVariable[];
  /** Perguntas que o "Salvar formulário" seguinte preenche — ver `formFieldsFilledLater`. */
  filledLater?: Record<string, string>;
}

// ============================================================================
// Icon + Color mapping
// ============================================================================

const NODE_ICONS: Record<string, IconName> = {
  trigger_event: 'zap',
  trigger_schedule: 'clock',
  trigger_webhook: 'globe',
  trigger_manual: 'play',
  trigger_date_field: 'calendar',
  trigger_inactivity: 'moon',
  trigger_instagram_comment: 'camera',
  trigger_instagram_mention: 'instagram',
  action_send_message: 'send',
  action_send_email: 'mail',
  action_send_template: 'file-text',
  action_send_media: 'image',
  action_http_request: 'link',
  action_query_database: 'database',
  action_create_lead: 'user-plus',
  action_update_lead: 'edit',
  action_update_contact: 'user',
  action_add_tag: 'tag',
  action_remove_tag: 'tag',
  action_assign: 'users',
  action_create_conversation: 'message-square',
  action_transfer_conversation: 'corner-up-right',
  action_satisfaction_survey: 'star',
  action_ask_question: 'help-circle',
  action_ask_form: 'list-ordered',
  action_next_number: 'hash',
  action_save_form_response: 'save',
  action_create_ticket: 'file-text',
  action_set_variable: 'edit',
  action_internal_notification: 'bell',
  action_find_leads: 'search',
  action_create_database_document: 'database',
  action_create_checklist: 'check-square',
  action_voice_clone: 'mic',
  action_voice_tts: 'music',
  action_voice_clone_delete: 'trash',
  action_mirror_media: 'download',
  control_if: 'git-branch',
  control_switch: 'git-branch',
  control_wait_for: 'clock',
  control_loop: 'repeat',
  control_split: 'target',
  control_retry_scope: 'refresh',
  ai_agent: 'cpu',
  ai_agent_inline: 'zap',
};

function getNodeColor(nodeType: string): string {
  if (nodeType.startsWith('trigger_')) return '#8b5cf6';
  if (nodeType.startsWith('action_')) return '#3b82f6';
  if (nodeType.startsWith('control_')) return '#f59e0b';
  if (nodeType.startsWith('ai_')) return '#10b981';
  return '#64748b';
}

// ============================================================================
// Component
// ============================================================================

export const NodeConfigPanel: React.FC<NodeConfigPanelProps> = ({
  nodeId,
  nodeType,
  nodeLabel,
  config,
  onChange,
  onLabelChange,
  onClose,
  availableContext,
  cancellation,
  onCancellationChange,
  filters,
  onFiltersChange,
  belowCancelledOutput = false,
  savedVariables,
  filledLater,
}) => {
  const ConfigComponent = getConfigComponent(nodeType);
  const color = getNodeColor(nodeType);
  const iconName = NODE_ICONS[nodeType] || 'settings';
  const context = availableContext || EMPTY_CONTEXT;
  const [tab, setTab] = useState<NodeConfigTab>('general');
  const isTriggerWithFilters = TRIGGERS_WITH_FILTERS.has(nodeType);
  const showFilters = isTriggerWithFilters || nodeTypeAcceptsFilters(nodeType);

  // Reseta para "Geral" ao trocar de node — senão o painel abre na aba que
  // ficou marcada no node anterior.
  useEffect(() => {
    setTab('general');
  }, [nodeId]);

  // Close on Escape
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  return createPortal(
    <>
      {/* Backdrop — dimmer visual apenas: config de node NÃO fecha ao clicar fora
          (DS §Overlays dismissal); fecha por X/Esc */}
      <div
        className="fixed inset-0 z-[200]"
        style={{ backgroundColor: 'rgba(0,0,0,0.3)' }}
      />

      {/* Popup — force light theme via CSS variable overrides */}
      <div
        className="fixed z-[201] flex flex-col"
        style={{
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 440,
          maxHeight: '80vh',
          backgroundColor: '#ffffff',
          borderRadius: 14,
          boxShadow: '0 25px 60px rgba(0,0,0,0.25)',
          border: '1px solid #e5e7eb',
          overflow: 'hidden',
          // Override CSS variables to light theme (popups are always light)
          '--theme-text': '#1e293b',
          '--theme-muted-text': '#6b7280',
          '--theme-input-background': '#ffffff',
          '--theme-input-text': '#1e293b',
          '--theme-input-border': 'rgba(0, 0, 0, 0.10)',
          '--theme-input-focus': '#8b5cf6',
          '--theme-error-color': '#ef4444',
          '--theme-card-background': '#ffffff',
          '--theme-card-text': '#334155',
          '--theme-card-border': 'rgba(0, 0, 0, 0.06)',
          '--theme-card-muted': '#94a3b8',
          '--theme-button-primary': '#8b5cf6',
          '--theme-button-primary-text': '#ffffff',
          '--theme-button-primary-hover': '#7c3aed',
          '--theme-primary': '#8b5cf6',
          '--theme-border': 'rgba(0, 0, 0, 0.06)',
          '--theme-component-background': '#ffffff',
          // Usado por chips/hover (ex: tags do MultiSelect em "Observar campos").
          // Sem este override, o valor escuro do tema vaza e as tags ficam com
          // fundo errado no dark mode dentro do popup (que é sempre claro).
          '--theme-button-ghost-hover': '#f1f5f9',
        } as React.CSSProperties}
      >
        {/* Header */}
        <div
          className="flex items-center gap-3 px-5 py-3.5 flex-shrink-0"
          style={{ borderBottom: '1px solid #f1f5f9' }}
        >
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: color }}
          >
            <Icon name={iconName} className="w-4 h-4" style={{ color: '#fff' }} />
          </div>
          <div className="flex-1 min-w-0">
            {onLabelChange ? (
              <Input
                data-testid="node-config-label"
                type="text"
                value={nodeLabel}
                onChange={(e) => onLabelChange(e.target.value)}
                className="text-sm font-semibold"
                placeholder="Nome do node"
              />
            ) : (
              <div className="text-sm font-semibold truncate" style={{ color: '#1e293b' }}>
                {nodeLabel}
              </div>
            )}
            <div className="text-[10px]" style={{ color: '#94a3b8' }}>
              {getNodeTypeLabel(nodeType)}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg transition-colors"
            style={{ color: '#94a3b8' }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
          >
            <FiX size={16} />
          </button>
        </div>

        {/* Config body */}
        <div className="flex-1 overflow-y-auto p-5">
          <NodeConfigTabs
            active={tab}
            onChange={setTab}
            showFilters={showFilters}
            filtersCount={
              isTriggerWithFilters
                ? (Array.isArray(config.filters) ? (config.filters as unknown[]).length : 0)
                : (filters?.length ?? 0)
            }
            cancellationCount={cancellation?.rules.length ?? 0}
          />

          <div className="mt-4">
            {tab === 'general' && (
              ConfigComponent ? (
                <Suspense
                  fallback={
                    <div className="text-xs text-center py-8" style={{ color: '#94a3b8' }}>
                      Carregando configuração...
                    </div>
                  }
                >
                  <ConfigComponent
                    config={config}
                    onChange={onChange}
                    availableContext={context}
                    nodeId={nodeId}
                    {...(savedVariables !== undefined ? { savedVariables } : {})}
                    {...(filledLater !== undefined ? { filledLater } : {})}
                  />
                </Suspense>
              ) : (
                <div className="text-xs text-center py-8" style={{ color: '#94a3b8' }}>
                  Nenhuma configuração disponível para este tipo de node.
                </div>
              )
            )}

            {tab === 'filters' && showFilters && (
              isTriggerWithFilters ? (
                <TriggerFiltersTab nodeType={nodeType} config={config} onChange={onChange} />
              ) : (
                <NodeFiltersTab
                  filters={filters ?? []}
                  onChange={(next) => onFiltersChange?.(next)}
                  context={context}
                  belowCancelledOutput={belowCancelledOutput}
                />
              )
            )}

            {tab === 'cancellation' && (
              <CancellationRulesEditor
                value={cancellation}
                onChange={onCancellationChange}
                availableContext={context}
                isTrigger={nodeType.startsWith('trigger_')}
                isWaitNode={nodeType === 'control_wait_for'}
              />
            )}
          </div>
        </div>

        {/* Footer — Fechar + Aplicar (mudanças já aplicam live via onChange) */}
        <div
          className="flex gap-2.5 px-4 py-3 flex-shrink-0"
          style={{ borderTop: '1px solid #eef1f5' }}
        >
          <button
            type="button"
            data-testid="node-config-close"
            onClick={onClose}
            className="flex-1 h-[38px] rounded-[10px] text-[13.5px] font-semibold transition-colors"
            style={{ border: '1px solid #d7dde5', color: '#475569' }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
          >
            Fechar
          </button>
          <button
            type="button"
            data-testid="node-config-apply"
            onClick={onClose}
            className="flex-1 h-[38px] rounded-[10px] text-[13.5px] font-semibold inline-flex items-center justify-center gap-1.5 transition-colors"
            style={{ backgroundColor: '#8b5cf6', color: '#ffffff' }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#7c3aed'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#8b5cf6'; }}
          >
            <Icon name="check" className="w-4 h-4" />
            Aplicar
          </button>
        </div>
      </div>
    </>,
    document.body
  );
};

export default NodeConfigPanel;
